import { useState, useCallback, useEffect, useRef } from "react";
import { clampTime } from "@src/packages/utils/time";
import {
  View,
  Text,
  Pressable,
  Platform,
  ScrollView,
  TextInput,
} from "react-native";
import {
  FileText,
  Check,
  AlertCircle,
  FolderOpen,
  Shield,
  PenTool,
  Receipt,
  ChevronLeft,
  Edit3,
  Trash2,
  Plus,
  Microscope,
  ScanLine,
  ClipboardList,
  Dna,
  File,
  ChevronDown,
  CalendarClock,
} from "lucide-react-native";
import * as DocumentPicker from "expo-document-picker";
import {
  BrownButton,
  Dialog,
  DialogPrimaryButton,
  IconButton,
  MoonDatePicker,
  MoonFilterChip,
} from "@louise/moon-ui";
import {
  useGetUploadUrl,
  useUploadFile,
  useCompleteUpload,
} from "@src/packages/documents/hooks/useDocuments";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  useCreateAppointment,
  useCreateMedication,
} from "@src/packages/care/useCalendarData";
import { serializeMedicationRoute } from "@src/packages/care/medication-route";
import { serializeMedicationForm } from "@src/packages/care/medication-form";
import { endDateFromDuration } from "@src/packages/document-vault/medication-duration";
import { useMedications } from "@src/packages/care/hooks/useMedications";
import {
  useCreateTestResult,
  useCreateTestResults,
} from "@src/packages/test-results/hooks/useTestResults";
import type {
  TestResultType,
  BloodTestCategory,
  ImagingTestCategory,
  GeneticsTestCategory,
} from "@src/packages/test-results/hooks/useTestResults";
import {
  readFileForUpload,
  resolveUploadContentType,
} from "@src/packages/utils/fileUpload";
import {
  HORMONAL_MARKERS,
  UNITS,
} from "@src/packages/document-vault/add-document/review-forms/marker-data";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "@src/packages/moon-ui/theme/ember";
import {
  attachUploadJobSourceUpload,
  createUploadJob,
  createUploadJobIdempotencyKey,
  destinationForUploadContext,
  sendUploadJobCommand,
  type UploadJobSourceContext,
} from "@src/packages/documents/ingestion";
import {
  projectDocumentProcessingRunToPrefill,
  waitForDocumentProcessingRun,
} from "@src/packages/documents/ingestion/documentProcessingRun";
import {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
import {
  DocumentUploadProcessingStep,
  type DocumentUploadProcessingItem,
} from "@src/packages/document-vault/DocumentUploadProcessingStep";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export {
  DocumentFileDropZone,
  createSelectedFileFromWebFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
export type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";

// ── Marker & unit options ──

const MARKER_OPTIONS = HORMONAL_MARKERS.map((m) => m.name);

function defaultUnitForMarker(marker: string): string | null {
  const found = HORMONAL_MARKERS.find(
    (m) => m.name.toLowerCase() === marker.toLowerCase(),
  );
  return found?.defaultUnit ?? null;
}

// ── Types ──

export type DocumentSlotInfo = {
  id: string;
  name: string;
  why: string;
} | null;

type Props = {
  visible: boolean;
  onClose: () => void;
  onSave?: (result: DocumentUploadSaveResult) => void;
  slot?: DocumentSlotInfo;
  defaultCategory?: string | null;
  defaultTags?: string[];
  ingestionContext?: UploadJobSourceContext;
  autoPickOnOpen?: boolean;
  initialFile?: SelectedFile | null;
  initialFiles?: SelectedFile[] | null;
  onMonitoringExtracted?: (data: Record<string, unknown>) => void;
};

export type DocumentUploadSaveResult = {
  fileName: string;
  resultType: string;
  category: string | null;
  uploadId: string | null;
  documentId: string | null;
};

type Step =
  | "pick"
  | "uploading"
  | "reading"
  | "review"
  | "monitoringParsed"
  | "processingError"
  | "saving"
  | "success";

type OCRData = Record<string, unknown>;

type ResultRow = {
  id: string;
  marker: string;
  value: string;
  unit: string;
  referenceRange: string;
};

type MedicationRow = {
  id: string;
  name: string;
  dosage: string;
  dosageUnit: string;
  frequency: string;
  route: string;
  /** OCR-extracted delivery method (e.g. "pre_filled_pen"); serialized to the API form enum on create. */
  form: string;
  startDate: string;
  endDate: string;
  timeOfDay: string;
};

type AppointmentRow = {
  id: string;
  title: string;
  type: string; // "exam" | "procedure" | "consult" | "counseling"
  date: string;
  time: string;
  notes: string;
};

type DurableUploadJobItem = {
  jobId: string;
  fileItemId: string;
};

const APPOINTMENT_TYPE_OPTIONS = ["exam", "procedure", "consult", "counseling"];

type CategoryOption = {
  key: string;
  labelKey: string;
  Icon: typeof FolderOpen;
};

export const DOCUMENT_UPLOAD_CATEGORIES: CategoryOption[] = [
  {
    key: "medical_records",
    labelKey: "documents.category.medical_records.short_label",
    Icon: FolderOpen,
  },
  {
    key: "coverage_benefits",
    labelKey: "documents.category.coverage_benefits.short_label",
    Icon: Shield,
  },
  {
    key: "admin",
    labelKey: "documents.category.admin.short_label",
    Icon: Receipt,
  },
  {
    key: "legal_consent",
    labelKey: "documents.category.legal_consent.short_label",
    Icon: PenTool,
  },
];

// ── Document type labels ──

type DocumentType =
  | "lab"
  | "imaging"
  | "treatment_plan"
  | "appointment_letter"
  | "genetic"
  | "other";

const DOCUMENT_TYPE_META: Record<
  DocumentType,
  { labelKey: string; color: string; icon: typeof FileText }
> = {
  lab: {
    labelKey: "documents.doc_type.lab",
    color: "#6366F1",
    icon: Microscope,
  },
  imaging: {
    labelKey: "documents.doc_type.imaging",
    color: "#0EA5E9",
    icon: ScanLine,
  },
  treatment_plan: {
    labelKey: "documents.doc_type.treatment_plan",
    color: "#F59E0B",
    icon: ClipboardList,
  },
  appointment_letter: {
    labelKey: "documents.doc_type.appointment_letter",
    color: "#10B981",
    icon: CalendarClock,
  },
  genetic: {
    labelKey: "documents.doc_type.genetic",
    color: "#A855F7",
    icon: Dna,
  },
  other: { labelKey: "documents.doc_type.other", color: "#78716C", icon: File },
};

const ALL_DOCUMENT_TYPES: DocumentType[] = [
  "lab",
  "imaging",
  "treatment_plan",
  "appointment_letter",
  "genetic",
  "other",
];

/** Map OCR resultType to our DocumentType */
function ocrTypeToDocType(resultType: string): DocumentType {
  switch (resultType) {
    case "blood":
    case "semen":
      return "lab";
    case "imaging":
      return "imaging";
    case "genetic":
      return "genetic";
    case "prescription":
      return "treatment_plan";
    case "appointment_letter":
      return "appointment_letter";
    default:
      return "other";
  }
}

/** Truthy only when the value is a non-empty string. The OCR fills missing
 *  fields with empty strings (not nulls), so plain `if (value)` checks
 *  misclassify documents — e.g. a prescription with `imagingType: ""` was
 *  routed to the imaging form. */
function hasContent(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function monitoringValueCount(data: OCRData | null): number {
  if (!data) return 0;
  const fieldCount = [
    "leftFollicleSizes",
    "rightFollicleSizes",
    "leftFollicleCount",
    "rightFollicleCount",
    "endometrialThickness",
  ].filter((key) => hasContent(data[key])).length;
  const resultCount = Array.isArray(data.results)
    ? data.results.filter((result) => {
        if (!result || typeof result !== "object") return false;
        return hasContent((result as Record<string, unknown>).marker);
      }).length
    : 0;
  return fieldCount + resultCount;
}

/** Detect the primary content type of the OCR result.
 *  Medications & appointments are treated as supplementary — they can appear
 *  alongside any primary type and are always prefilled separately.
 *  Order matters: medications/appointments take priority so a prescription
 *  doesn't get misclassified as imaging when the LLM returns empty imaging
 *  fields. */
function detectResultType(data: OCRData): string {
  // Prescriptions and appointment letters drive the review form before
  // anything else — if the document mentions meds or appointments, that's the
  // user's intent even if other fields are loosely populated. The
  // PrescriptionReview form already handles both meds and appointments, so we
  // route both cases through the same `prescription` type.
  if (
    data.medications &&
    Array.isArray(data.medications) &&
    (data.medications as unknown[]).length > 0
  ) {
    return "prescription";
  }
  if (
    data.appointments &&
    Array.isArray(data.appointments) &&
    (data.appointments as unknown[]).length > 0
  ) {
    return "prescription";
  }

  // Primary clinical content types
  if (
    data.results &&
    Array.isArray(data.results) &&
    (data.results as unknown[]).length > 0
  ) {
    return "blood";
  }
  if (
    hasContent(data.antralFollicleCount) ||
    hasContent(data.endometrialThickness) ||
    hasContent(data.imagingType)
  ) {
    return "imaging";
  }
  if (hasContent(data.testType) || hasContent(data.geneticSummary)) {
    return "genetic";
  }
  if (
    hasContent(data.volume) ||
    hasContent(data.concentration) ||
    hasContent(data.morphology)
  ) {
    return "semen";
  }
  if (
    hasContent(data.policyNumber) ||
    hasContent(data.claimNumber) ||
    hasContent(data.insurerName)
  ) {
    return "insurance";
  }
  if (hasContent(data.documentType)) {
    return "compliance";
  }
  return "general";
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function revokeSelectedFileObjectUrl(file: SelectedFile) {
  if (Platform.OS !== "web" || !file.objectUrl || typeof URL === "undefined") {
    return;
  }
  URL.revokeObjectURL(file.objectUrl);
}

function getDocumentUploadErrorMessage(error: unknown, fallback: string) {
  if (!(error instanceof Error)) return fallback;
  const message = error.message.trim();
  return message && message !== "undefined" ? message : fallback;
}

// ── Editable field component ──

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  multiline,
  onBlur,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  onBlur?: () => void;
}) {
  return (
    <View style={{ gap: 4 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "600",
          color: EMBER.colors.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        placeholder={placeholder}
        placeholderTextColor={EMBER.colors.textFaint}
        multiline={multiline}
        style={{
          fontSize: 14,
          color: EMBER.colors.text,
          fontFamily: "DMSans_400Regular",
          backgroundColor: "rgba(0,0,0,0.03)",
          borderRadius: 8,
          paddingHorizontal: 12,
          paddingVertical: 10,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.06)",
          ...(multiline && { minHeight: 60, textAlignVertical: "top" }),
          ...(Platform.OS === "web" && { outlineStyle: "none" }),
        }}
      />
    </View>
  );
}

// ── Step 1: Pick file ──

function PickStep({
  onClose,
  slot,
  defaultCategory,
  onFilesSelected,
  errorMessage,
  autoPickOnOpen = true,
}: {
  onClose: () => void;
  slot: DocumentSlotInfo;
  defaultCategory?: string | null;
  onFilesSelected: (files: SelectedFile[], category: string | null) => void;
  errorMessage: string | null;
  autoPickOnOpen?: boolean;
}) {
  const t = useTranslations();
  const autoPickHandledRef = useRef(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    slot ? null : (defaultCategory ?? null),
  );

  const handlePick = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        onFilesSelected(
          result.assets.map((file) => ({
            name: file.name,
            uri: file.uri,
            mimeType: resolveUploadContentType({
              name: file.name,
              uri: file.uri,
              mimeType: file.mimeType,
            }),
            size: file.size ?? undefined,
          })),
          selectedCategory,
        );
      }
    } catch (error) {
      console.error("Error picking document:", error);
    }
  }, [onFilesSelected, selectedCategory]);

  useEffect(() => {
    if (!autoPickOnOpen || autoPickHandledRef.current) return;
    autoPickHandledRef.current = true;
    void handlePick();
  }, [autoPickOnOpen, handlePick]);

  return (
    <>
      {/* Close button */}
      <CloseButton onClose={onClose} />

      {/* Header */}
      {slot ? (
        <View style={{ marginBottom: 20, paddingRight: 32 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 20,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {slot.name}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textLight,
              marginTop: 4,
              lineHeight: 18,
            }}
          >
            {slot.why}
          </Text>
        </View>
      ) : (
        <View style={{ marginBottom: 20, paddingRight: 32 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 20,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {t("documents.upload.header_title")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textLight,
              marginTop: 4,
              lineHeight: 18,
            }}
          >
            {t("documents.upload.header_subtitle")}
          </Text>
        </View>
      )}

      {/* Error */}
      {errorMessage && <ErrorBanner message={errorMessage} />}

      {/* Category pills — only for general uploads */}
      {!slot && (
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {DOCUMENT_UPLOAD_CATEGORIES.map((cat) => {
            const isSelected = selectedCategory === cat.key;
            return (
              <MoonFilterChip
                key={cat.key}
                label={t(cat.labelKey)}
                icon={cat.Icon}
                selected={isSelected}
                onPress={() => setSelectedCategory(isSelected ? null : cat.key)}
                showCheck={false}
              />
            );
          })}
        </View>
      )}

      {/* File picker area */}
      <DocumentFileDropZone
        onPick={handlePick}
        onFilesSelected={(files) => onFilesSelected(files, selectedCategory)}
      />
    </>
  );
}

function ProcessingErrorStep({
  onClose,
  fileName,
  message,
  onChooseAnother,
}: {
  onClose: () => void;
  fileName: string;
  message: string;
  onChooseAnother: () => void;
}) {
  const t = useTranslations();

  return (
    <>
      <CloseButton onClose={onClose} />
      <View
        style={{
          alignItems: "center",
          justifyContent: "center",
          paddingVertical: 28,
          gap: 14,
        }}
      >
        <View
          style={{
            width: 52,
            height: 52,
            borderRadius: 26,
            backgroundColor: `${EMBER.colors.coral}14`,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AlertCircle size={25} color={EMBER.colors.coral} strokeWidth={2} />
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 18,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
          }}
        >
          {t("documents.upload.error_failed_process")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 18,
            color: EMBER.colors.textLight,
            textAlign: "center",
          }}
        >
          {message}
        </Text>
        <View
          style={{
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            padding: 10,
            borderRadius: 8,
            backgroundColor: "rgba(0,0,0,0.02)",
          }}
        >
          <FileText
            size={14}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{ flex: 1, fontSize: 12, color: EMBER.colors.textMid }}
          >
            {fileName}
          </Text>
        </View>
        <DialogPrimaryButton
          label={t("documents.upload.failure.choose_another")}
          onPress={onChooseAnother}
          style={{ width: "100%", marginTop: 4 }}
        />
      </View>
    </>
  );
}

function QueuedDocumentsCard({ files }: { files: SelectedFile[] }) {
  const t = useTranslations();
  if (files.length === 0) return null;

  const visibleFiles = files.slice(0, 3);
  const hiddenFileCount = files.length - visibleFiles.length;

  return (
    <View
      style={{
        gap: 8,
        padding: 10,
        borderRadius: 8,
        backgroundColor: "rgba(0,0,0,0.02)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.05)",
      }}
    >
      <View style={{ gap: 2 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: EMBER.colors.text,
          }}
        >
          {t("documents.upload.queued_documents_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: EMBER.colors.textLight,
          }}
        >
          {t("documents.upload.queued_documents_subtitle")}
        </Text>
      </View>
      <View style={{ gap: 6 }}>
        {visibleFiles.map((file) => (
          <View
            key={file.uri}
            style={{ flexDirection: "row", alignItems: "center", gap: 7 }}
          >
            <FileText
              size={14}
              color={EMBER.colors.textLight}
              strokeWidth={1.6}
            />
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{ flex: 1, fontSize: 12, color: EMBER.colors.text }}
            >
              {file.name}
            </Text>
          </View>
        ))}
        {hiddenFileCount > 0 ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 11, color: EMBER.colors.textLight }}
          >
            {t("documents.upload.queued_documents_more", {
              params: { count: String(hiddenFileCount) },
            })}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function MonitoringParsedStep({
  onClose,
  fileName,
  valueCount,
  queuedFiles,
  saving,
  onContinue,
}: {
  onClose: () => void;
  fileName: string;
  valueCount: number;
  queuedFiles: SelectedFile[];
  saving: boolean;
  onContinue: () => void;
}) {
  const t = useTranslations();
  const hasValues = valueCount > 0;

  return (
    <>
      <CloseButton onClose={onClose} />
      <View
        style={{
          alignItems: "center",
          justifyContent: "center",
          paddingVertical: 22,
          gap: 12,
        }}
      >
        <View
          style={{
            width: 52,
            height: 52,
            borderRadius: 26,
            backgroundColor: `${EMBER.colors.green}18`,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Check size={25} color={EMBER.colors.green} strokeWidth={2.4} />
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 18,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
          }}
        >
          {t(
            hasValues
              ? "documents.upload.monitoring_parsed_title"
              : "documents.upload.monitoring_parsed_empty_title",
          )}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 18,
            color: EMBER.colors.textLight,
            textAlign: "center",
          }}
        >
          {t(
            hasValues
              ? "documents.upload.monitoring_parsed_subtitle"
              : "documents.upload.monitoring_parsed_empty_subtitle",
          )}
        </Text>
        <View
          style={{
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            padding: 10,
            borderRadius: 8,
            backgroundColor: "rgba(0,0,0,0.02)",
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.05)",
          }}
        >
          <FileText
            size={14}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{ flex: 1, fontSize: 12, color: EMBER.colors.textMid }}
          >
            {fileName}
          </Text>
        </View>
        {hasValues ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 13, color: EMBER.colors.textLight }}
          >
            {t("documents.upload.monitoring_parsed_count", {
              params: { count: String(valueCount) },
            })}
          </Text>
        ) : null}
        {queuedFiles.length > 0 ? (
          <View style={{ width: "100%", marginTop: 4 }}>
            <QueuedDocumentsCard files={queuedFiles} />
          </View>
        ) : null}
        <DialogPrimaryButton
          label={
            saving
              ? t("documents.upload.saving")
              : t("documents.upload.monitoring_parsed_action")
          }
          onPress={onContinue}
          loading={saving}
          disabled={saving}
          style={{ width: "100%", marginTop: 4 }}
        />
      </View>
    </>
  );
}

function sanitizeNumeric(v: string): string {
  return v.replace(/[^0-9.\-]/g, "");
}

// ── Step 3: Review extracted data ──

function ChipGrid({
  options,
  selected,
  onSelect,
}: {
  options: string[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  const capturePress = usePressCapture("DocumentChipGrid");
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        paddingVertical: 8,
      }}
    >
      {options.map((option) => {
        const isSelected = option.toLowerCase() === selected.toLowerCase();
        return (
          <Pressable
            key={option}
            onPress={() => {
              capturePress(pressLabel(option), "document_chip");
              onSelect(option);
            }}
            style={{
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 16,
              backgroundColor: isSelected
                ? EMBER.colors.warm
                : "rgba(0,0,0,0.04)",
              borderWidth: 1,
              borderColor: isSelected ? EMBER.colors.warm : "rgba(0,0,0,0.06)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: isSelected ? "#fff" : EMBER.colors.textMid,
                fontWeight: isSelected ? "600" : "400",
              }}
            >
              {option}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function BloodResultRow({
  row,
  onValueChange,
  onRemove,
  onTapMarker,
  onTapUnit,
  isMarkerActive,
  isUnitActive,
}: {
  row: ResultRow;
  onValueChange: (v: string) => void;
  onRemove: () => void;
  onTapMarker: () => void;
  onTapUnit: () => void;
  isMarkerActive: boolean;
  isUnitActive: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("BloodResultRow");
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        height: 40,
        backgroundColor:
          isMarkerActive || isUnitActive
            ? `${EMBER.colors.warm}06`
            : "rgba(0,0,0,0.02)",
        borderRadius: 10,
        borderWidth: 1,
        borderColor:
          isMarkerActive || isUnitActive
            ? `${EMBER.colors.warm}25`
            : "rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}
    >
      {/* Marker */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(row.marker, t("documents.upload.select_placeholder")),
            "pick_result_marker",
          );
          onTapMarker();
        }}
        style={{ flex: 3, paddingHorizontal: 10, justifyContent: "center" }}
      >
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 13,
            fontWeight: "500",
            color: row.marker
              ? isMarkerActive
                ? EMBER.colors.warm
                : EMBER.colors.text
              : EMBER.colors.textFaint,
          }}
        >
          {row.marker || t("documents.upload.select_placeholder")}
        </Text>
      </Pressable>

      <View
        style={{ width: 1, height: 20, backgroundColor: "rgba(0,0,0,0.06)" }}
      />

      {/* Value */}
      <TextInput
        value={row.value}
        onChangeText={(v) => onValueChange(sanitizeNumeric(v))}
        placeholder="—"
        placeholderTextColor={EMBER.colors.textFaint}
        keyboardType="decimal-pad"
        style={{
          width: 52,
          fontSize: 15,
          fontWeight: "700",
          color: EMBER.colors.text,
          fontFamily: "DMSans_700Bold",
          textAlign: "center",
          paddingHorizontal: 2,
          paddingVertical: 0,
          ...(Platform.OS === "web" && { outlineStyle: "none" }),
        }}
      />

      <View
        style={{ width: 1, height: 20, backgroundColor: "rgba(0,0,0,0.06)" }}
      />

      {/* Unit */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(row.unit, t("documents.upload.unit_placeholder")),
            "pick_result_unit",
          );
          onTapUnit();
        }}
        style={{ flex: 2, paddingHorizontal: 8, justifyContent: "center" }}
      >
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 12,
            color: row.unit
              ? isUnitActive
                ? EMBER.colors.warm
                : EMBER.colors.textLight
              : EMBER.colors.textFaint,
          }}
        >
          {row.unit || t("documents.upload.unit_placeholder")}
        </Text>
      </Pressable>

      {/* Remove */}
      <Pressable
        onPress={() => {
          // Trash icon with no visible copy to name it.
          capturePress("remove_result_row");
          onRemove();
        }}
        hitSlop={4}
        style={{ width: 32, alignItems: "center", justifyContent: "center" }}
      >
        <Trash2 size={13} color={EMBER.colors.textFaint} strokeWidth={1.5} />
      </Pressable>
    </View>
  );
}

function BloodTestReview({
  data,
  rows,
  setRows,
  testName,
  setTestName,
  testDate,
  setTestDate,
  labName,
  setLabName,
}: {
  data: OCRData;
  rows: ResultRow[];
  setRows: (rows: ResultRow[]) => void;
  testName: string;
  setTestName: (v: string) => void;
  testDate: string;
  setTestDate: (v: string) => void;
  labName: string;
  setLabName: (v: string) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("BloodTestReview");
  const [activePicker, setActivePicker] = useState<{
    rowId: string;
    field: "marker" | "unit";
  } | null>(null);

  const updateRow = (id: string, field: keyof ResultRow, value: string) => {
    setRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleChipSelect = (value: string) => {
    if (!activePicker) return;
    if (activePicker.field === "marker") {
      const defUnit = defaultUnitForMarker(value);
      setRows(
        rows.map((r) => {
          if (r.id !== activePicker.rowId) return r;
          return {
            ...r,
            marker: value,
            unit: defUnit && !r.unit ? defUnit : r.unit,
          };
        }),
      );
    } else {
      updateRow(activePicker.rowId, "unit", value);
    }
    setActivePicker(null);
  };

  const removeRow = (id: string) => {
    setRows(rows.filter((r) => r.id !== id));
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        id: `new-${Date.now()}`,
        marker: "",
        value: "",
        unit: "",
        referenceRange: "",
      },
    ]);
  };

  return (
    <View style={{ gap: 14 }}>
      <Field
        label={t("documents.upload.fields.test_name")}
        value={testName}
        onChangeText={setTestName}
      />
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.date")}
            value={testDate}
            onChangeText={setTestDate}
            placeholder={t("documents.upload.fields.date_placeholder")}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.lab_clinic")}
            value={labName}
            onChangeText={setLabName}
          />
        </View>
      </View>

      {/* Results table */}
      <View style={{ gap: 6, marginTop: 4 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            color: EMBER.colors.textLight,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {t("documents.upload.results_label")}
        </Text>
        {rows.length > 0 && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                flex: 3,
                fontSize: 10,
                color: EMBER.colors.textFaint,
                fontWeight: "500",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                paddingHorizontal: 10,
              }}
            >
              {t("documents.upload.table.marker")}
            </Text>
            <View style={{ width: 1 }} />
            <Text
              className="font-dm-sans"
              style={{
                width: 52,
                fontSize: 10,
                color: EMBER.colors.textFaint,
                fontWeight: "500",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                textAlign: "center",
              }}
            >
              {t("documents.upload.table.value")}
            </Text>
            <View style={{ width: 1 }} />
            <Text
              className="font-dm-sans"
              style={{
                flex: 2,
                fontSize: 10,
                color: EMBER.colors.textFaint,
                fontWeight: "500",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                paddingLeft: 10,
              }}
            >
              {t("documents.upload.table.unit")}
            </Text>
            <View style={{ width: 32 }} />
          </View>
        )}
        {rows.map((row) => (
          <BloodResultRow
            key={row.id}
            row={row}
            onValueChange={(v: string) => updateRow(row.id, "value", v)}
            onRemove={() => removeRow(row.id)}
            onTapMarker={() =>
              setActivePicker(
                activePicker?.rowId === row.id &&
                  activePicker?.field === "marker"
                  ? null
                  : { rowId: row.id, field: "marker" },
              )
            }
            onTapUnit={() =>
              setActivePicker(
                activePicker?.rowId === row.id && activePicker?.field === "unit"
                  ? null
                  : { rowId: row.id, field: "unit" },
              )
            }
            isMarkerActive={
              activePicker?.rowId === row.id && activePicker?.field === "marker"
            }
            isUnitActive={
              activePicker?.rowId === row.id && activePicker?.field === "unit"
            }
          />
        ))}

        {/* Chip grid — shows below rows when a marker/unit cell is tapped */}
        {activePicker && (
          <ChipGrid
            options={activePicker.field === "marker" ? MARKER_OPTIONS : UNITS}
            selected={
              rows.find((r) => r.id === activePicker.rowId)?.[
                activePicker.field === "marker" ? "marker" : "unit"
              ] ?? ""
            }
            onSelect={handleChipSelect}
          />
        )}

        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("documents.upload.add_marker")),
              "documents.upload.add_marker",
            );
            addRow();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            paddingVertical: 8,
            borderRadius: 8,
            borderWidth: 1,
            borderStyle: "dashed",
            borderColor: "rgba(0,0,0,0.08)",
          }}
        >
          <Plus size={14} color={EMBER.colors.warm} strokeWidth={2} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "500",
              color: EMBER.colors.warm,
            }}
          >
            {t("documents.upload.add_marker")}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

function ResultUnitRow({
  resultValue,
  setResultValue,
  resultUnit,
  setResultUnit,
  label,
  placeholder,
}: {
  resultValue: string;
  setResultValue: (v: string) => void;
  resultUnit: string;
  setResultUnit: (v: string) => void;
  label?: string;
  placeholder?: string;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 4 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "600",
          color: EMBER.colors.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label ?? t("documents.upload.fields.result_default_label")}
      </Text>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          backgroundColor: "rgba(0,0,0,0.03)",
          borderRadius: 8,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <TextInput
          value={resultValue}
          onChangeText={setResultValue}
          placeholder={
            placeholder ?? t("documents.upload.fields.result_placeholder")
          }
          placeholderTextColor={EMBER.colors.textFaint}
          keyboardType="decimal-pad"
          style={{
            flex: 2,
            fontSize: 16,
            fontWeight: "600",
            color: EMBER.colors.text,
            fontFamily: "DMSans_700Bold",
            paddingHorizontal: 12,
            paddingVertical: 10,
            ...(Platform.OS === "web" && { outlineStyle: "none" }),
          }}
        />
        <View
          style={{
            width: 1,
            height: 24,
            backgroundColor: "rgba(0,0,0,0.08)",
          }}
        />
        <TextInput
          value={resultUnit}
          onChangeText={setResultUnit}
          placeholder={t("documents.upload.fields.unit")}
          placeholderTextColor={EMBER.colors.textFaint}
          style={{
            flex: 1,
            fontSize: 13,
            color: EMBER.colors.textLight,
            fontFamily: "DMSans_400Regular",
            paddingHorizontal: 12,
            paddingVertical: 10,
            ...(Platform.OS === "web" && { outlineStyle: "none" }),
          }}
        />
      </View>
    </View>
  );
}

function ImagingReview({
  examName,
  setExamName,
  examDate,
  setExamDate,
  clinicName,
  setClinicName,
  imagingType,
  setImagingType,
  findings,
  setFindings,
  afc,
  setAfc,
  afcUnit,
  setAfcUnit,
  endoThickness,
  setEndoThickness,
  endoUnit,
  setEndoUnit,
}: {
  examName: string;
  setExamName: (v: string) => void;
  examDate: string;
  setExamDate: (v: string) => void;
  clinicName: string;
  setClinicName: (v: string) => void;
  imagingType: string;
  setImagingType: (v: string) => void;
  findings: string;
  setFindings: (v: string) => void;
  afc: string;
  setAfc: (v: string) => void;
  afcUnit: string;
  setAfcUnit: (v: string) => void;
  endoThickness: string;
  setEndoThickness: (v: string) => void;
  endoUnit: string;
  setEndoUnit: (v: string) => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 14 }}>
      <Field
        label={t("documents.upload.fields.exam_name")}
        value={examName}
        onChangeText={setExamName}
      />
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.date")}
            value={examDate}
            onChangeText={setExamDate}
            placeholder={t("documents.upload.fields.date_placeholder")}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.clinic")}
            value={clinicName}
            onChangeText={setClinicName}
          />
        </View>
      </View>
      <Field
        label={t("documents.upload.fields.imaging_type")}
        value={imagingType}
        onChangeText={setImagingType}
      />
      <Field
        label={t("documents.upload.fields.findings")}
        value={findings}
        onChangeText={setFindings}
        multiline
      />
      <ResultUnitRow
        label={t("documents.upload.fields.afc")}
        resultValue={afc}
        setResultValue={setAfc}
        resultUnit={afcUnit}
        setResultUnit={setAfcUnit}
        placeholder={t("documents.upload.fields.afc_placeholder")}
      />
      <ResultUnitRow
        label={t("documents.upload.fields.endo_thickness")}
        resultValue={endoThickness}
        setResultValue={setEndoThickness}
        resultUnit={endoUnit}
        setResultUnit={setEndoUnit}
        placeholder={t("documents.upload.fields.endo_placeholder")}
      />
    </View>
  );
}

function GeneticReview({
  testName,
  setTestName,
  testDate,
  setTestDate,
  labName,
  setLabName,
  testType,
  setTestType,
  result,
  setResult,
  summary,
  setSummary,
}: {
  testName: string;
  setTestName: (v: string) => void;
  testDate: string;
  setTestDate: (v: string) => void;
  labName: string;
  setLabName: (v: string) => void;
  testType: string;
  setTestType: (v: string) => void;
  result: string;
  setResult: (v: string) => void;
  summary: string;
  setSummary: (v: string) => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 14 }}>
      <Field
        label={t("documents.upload.fields.test_name")}
        value={testName}
        onChangeText={setTestName}
      />
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.date")}
            value={testDate}
            onChangeText={setTestDate}
            placeholder={t("documents.upload.fields.date_placeholder")}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.lab")}
            value={labName}
            onChangeText={setLabName}
          />
        </View>
      </View>
      <Field
        label={t("documents.upload.fields.test_type")}
        value={testType}
        onChangeText={setTestType}
        placeholder={t("documents.upload.fields.test_type_placeholder")}
      />
      <Field
        label={t("documents.upload.fields.result")}
        value={result}
        onChangeText={setResult}
      />
      <Field
        label={t("documents.upload.fields.summary")}
        value={summary}
        onChangeText={setSummary}
        multiline
      />
    </View>
  );
}

function SemenReview({
  testName,
  setTestName,
  testDate,
  setTestDate,
  labName,
  setLabName,
  volume,
  setVolume,
  concentration,
  setConcentration,
  motility,
  setMotility,
  morphology,
  setMorphology,
  notes,
  setNotes,
}: {
  testName: string;
  setTestName: (v: string) => void;
  testDate: string;
  setTestDate: (v: string) => void;
  labName: string;
  setLabName: (v: string) => void;
  volume: string;
  setVolume: (v: string) => void;
  concentration: string;
  setConcentration: (v: string) => void;
  motility: string;
  setMotility: (v: string) => void;
  morphology: string;
  setMorphology: (v: string) => void;
  notes: string;
  setNotes: (v: string) => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 14 }}>
      <Field
        label={t("documents.upload.fields.test_name")}
        value={testName}
        onChangeText={setTestName}
      />
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.date")}
            value={testDate}
            onChangeText={setTestDate}
            placeholder={t("documents.upload.fields.date_placeholder")}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.lab")}
            value={labName}
            onChangeText={setLabName}
          />
        </View>
      </View>
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.volume")}
            value={volume}
            onChangeText={setVolume}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.concentration")}
            value={concentration}
            onChangeText={setConcentration}
          />
        </View>
      </View>
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.motility")}
            value={motility}
            onChangeText={setMotility}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.morphology")}
            value={morphology}
            onChangeText={setMorphology}
          />
        </View>
      </View>
      <Field
        label={t("documents.upload.fields.notes")}
        value={notes}
        onChangeText={setNotes}
        multiline
      />
    </View>
  );
}

function PrescriptionReview({
  medications,
  setMedications,
  appointments,
  setAppointments,
  prescriber,
  setPrescriber,
  date,
  setDate,
  clinic,
  setClinic,
}: {
  medications: MedicationRow[];
  setMedications: (rows: MedicationRow[]) => void;
  appointments: AppointmentRow[];
  setAppointments: (rows: AppointmentRow[]) => void;
  prescriber: string;
  setPrescriber: (v: string) => void;
  date: string;
  setDate: (v: string) => void;
  clinic: string;
  setClinic: (v: string) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PrescriptionReview");
  const updateRow = (id: string, field: keyof MedicationRow, value: string) => {
    setMedications(
      medications.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const removeRow = (id: string) => {
    setMedications(medications.filter((r) => r.id !== id));
  };

  const addRow = () => {
    setMedications([
      ...medications,
      {
        id: `med-${Date.now()}`,
        name: "",
        dosage: "",
        dosageUnit: "",
        frequency: "",
        route: "",
        form: "",
        startDate: "",
        endDate: "",
        timeOfDay: "",
      },
    ]);
  };

  return (
    <View style={{ gap: 14 }}>
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.date")}
            value={date}
            onChangeText={setDate}
            placeholder={t("documents.upload.fields.date_placeholder")}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.prescriber")}
            value={prescriber}
            onChangeText={setPrescriber}
          />
        </View>
      </View>
      <Field
        label={t("documents.upload.fields.clinic")}
        value={clinic}
        onChangeText={setClinic}
      />

      {/* Medication rows */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "600",
          color: EMBER.colors.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {t("documents.upload.medications_count", {
          params: { count: String(medications.length) },
        })}
      </Text>
      {medications.map((row) => (
        <View
          key={row.id}
          style={{
            backgroundColor: "rgba(0,0,0,0.02)",
            borderRadius: 10,
            padding: 12,
            gap: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.05)",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: EMBER.colors.text,
                flex: 1,
              }}
              numberOfLines={1}
            >
              {row.name || t("documents.upload.unnamed_medication")}
            </Text>
            <Pressable
              onPress={() => {
                // Trash icon with no visible copy to name it.
                capturePress("remove_medication_row");
                removeRow(row.id);
              }}
              hitSlop={8}
            >
              <Trash2 size={14} color={EMBER.colors.coral} strokeWidth={1.5} />
            </Pressable>
          </View>
          <Field
            label={t("documents.upload.fields.medication_name")}
            value={row.name}
            onChangeText={(v) => updateRow(row.id, "name", v)}
          />
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.dosage")}
                value={row.dosage}
                onChangeText={(v) => updateRow(row.id, "dosage", v)}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.unit")}
                value={row.dosageUnit}
                onChangeText={(v) => updateRow(row.id, "dosageUnit", v)}
                placeholder={t("documents.upload.fields.unit_placeholder")}
              />
            </View>
          </View>
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.frequency")}
                value={row.frequency}
                onChangeText={(v) => updateRow(row.id, "frequency", v)}
                placeholder={t("documents.upload.fields.frequency_placeholder")}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.route")}
                value={row.route}
                onChangeText={(v) => updateRow(row.id, "route", v)}
                placeholder={t("documents.upload.fields.route_placeholder")}
              />
            </View>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.textLight,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {t("documents.upload.fields.schedule")}
          </Text>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              height: 40,
              backgroundColor: "rgba(0,0,0,0.02)",
              borderRadius: 8,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            <MoonDatePicker
              value={row.startDate}
              onChange={(v) => updateRow(row.id, "startDate", v)}
            />
            <View
              style={{
                width: 1,
                height: 20,
                backgroundColor: "rgba(0,0,0,0.06)",
              }}
            />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.textFaint }}
            >
              {t("documents.upload.fields.ends")}
            </Text>
            <MoonDatePicker
              value={row.endDate}
              onChange={(v) => updateRow(row.id, "endDate", v)}
            />
            <View
              style={{
                width: 1,
                height: 20,
                backgroundColor: "rgba(0,0,0,0.06)",
              }}
            />
            <TextInput
              value={row.timeOfDay}
              onChangeText={(v) => updateRow(row.id, "timeOfDay", v)}
              onBlur={() => {
                if (!row.timeOfDay) return;
                const clamped = clampTime(row.timeOfDay.trim());
                updateRow(row.id, "timeOfDay", clamped ?? "");
              }}
              placeholder={t("documents.upload.fields.time_placeholder")}
              placeholderTextColor={EMBER.colors.textFaint}
              style={{
                width: 52,
                fontSize: 12,
                color: EMBER.colors.textLight,
                fontFamily: "DMSans_400Regular",
                textAlign: "center",
                paddingVertical: 0,
                ...(Platform.OS === "web" && { outlineStyle: "none" }),
              }}
            />
          </View>
        </View>
      ))}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("documents.upload.add_medication")),
            "documents.upload.add_medication",
          );
          addRow();
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          paddingVertical: 10,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.08)",
          borderStyle: "dashed",
        }}
      >
        <Plus size={14} color={EMBER.colors.warm} strokeWidth={2} />
        <Text
          className="font-dm-sans"
          style={{ fontSize: 13, fontWeight: "500", color: EMBER.colors.warm }}
        >
          {t("documents.upload.add_medication")}
        </Text>
      </Pressable>

      {/* Appointment rows */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "600",
          color: EMBER.colors.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          marginTop: 8,
        }}
      >
        {t("documents.upload.appointments_count", {
          params: { count: String(appointments.length) },
        })}
      </Text>
      {appointments.map((row) => (
        <View
          key={row.id}
          style={{
            backgroundColor: "rgba(0,0,0,0.02)",
            borderRadius: 10,
            padding: 12,
            gap: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.05)",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: EMBER.colors.text,
                flex: 1,
              }}
              numberOfLines={1}
            >
              {row.title || t("documents.upload.unnamed_appointment")}
            </Text>
            <Pressable
              onPress={() => {
                // Trash icon with no visible copy to name it.
                capturePress("remove_appointment_row");
                setAppointments(appointments.filter((r) => r.id !== row.id));
              }}
              hitSlop={8}
            >
              <Trash2 size={14} color={EMBER.colors.coral} strokeWidth={1.5} />
            </Pressable>
          </View>
          <Field
            label={t("documents.upload.fields.appointment_title")}
            value={row.title}
            onChangeText={(v) =>
              setAppointments(
                appointments.map((r) =>
                  r.id === row.id ? { ...r, title: v } : r,
                ),
              )
            }
          />
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.appointment_type")}
                value={row.type}
                onChangeText={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, type: v } : r,
                    ),
                  )
                }
                placeholder={t(
                  "documents.upload.fields.appointment_type_placeholder",
                )}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.date")}
                value={row.date}
                onChangeText={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, date: v } : r,
                    ),
                  )
                }
                placeholder={t("documents.upload.fields.date_placeholder")}
              />
            </View>
          </View>
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.time")}
                value={row.time}
                onChangeText={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, time: v } : r,
                    ),
                  )
                }
                onBlur={() => {
                  if (!row.time) return;
                  const clamped = clampTime(row.time.trim());
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, time: clamped ?? "" } : r,
                    ),
                  );
                }}
                placeholder={t("documents.upload.fields.time_placeholder")}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.notes")}
                value={row.notes}
                onChangeText={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, notes: v } : r,
                    ),
                  )
                }
              />
            </View>
          </View>
        </View>
      ))}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("documents.upload.add_appointment")),
            "documents.upload.add_appointment",
          );
          setAppointments([
            ...appointments,
            {
              id: `appt-${Date.now()}`,
              title: "",
              type: "exam",
              date: "",
              time: "",
              notes: "",
            },
          ]);
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          paddingVertical: 10,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.08)",
          borderStyle: "dashed",
        }}
      >
        <Plus size={14} color={EMBER.colors.warm} strokeWidth={2} />
        <Text
          className="font-dm-sans"
          style={{ fontSize: 13, fontWeight: "500", color: EMBER.colors.warm }}
        >
          {t("documents.upload.add_appointment")}
        </Text>
      </Pressable>
    </View>
  );
}

function GeneralReview({
  title,
  setTitle,
  date,
  setDate,
  provider,
  setProvider,
  summary,
  setSummary,
}: {
  title: string;
  setTitle: (v: string) => void;
  date: string;
  setDate: (v: string) => void;
  provider: string;
  setProvider: (v: string) => void;
  summary: string;
  setSummary: (v: string) => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 14 }}>
      <Field
        label={t("documents.upload.fields.document_title")}
        value={title}
        onChangeText={setTitle}
      />
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.date")}
            value={date}
            onChangeText={setDate}
            placeholder={t("documents.upload.fields.date_placeholder")}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.provider")}
            value={provider}
            onChangeText={setProvider}
          />
        </View>
      </View>
      <Field
        label={t("documents.upload.fields.summary")}
        value={summary}
        onChangeText={setSummary}
        multiline
      />
    </View>
  );
}

// ── Shared UI components ──

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <IconButton
      onPress={onClose}
      hitSlop={12}
      size={28}
      accessibilityLabel="Close upload modal"
      pressedOpacity={1}
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 2,
      }}
    >
      <Text
        style={{
          fontSize: 18,
          fontWeight: "400",
          color: EMBER.colors.textFaint,
        }}
      >
        ✕
      </Text>
    </IconButton>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        padding: 12,
        borderRadius: 10,
        backgroundColor: `${EMBER.colors.coral}10`,
        borderWidth: 1,
        borderColor: `${EMBER.colors.coral}30`,
        marginBottom: 16,
      }}
    >
      <AlertCircle size={16} color={EMBER.colors.coral} strokeWidth={1.5} />
      <Text
        className="font-dm-sans"
        style={{
          flex: 1,
          fontSize: 12,
          color: EMBER.colors.coral,
          lineHeight: 17,
        }}
      >
        {message}
      </Text>
    </View>
  );
}

// ── Main orchestrator ──

function ModalContent({
  onClose,
  onSave,
  slot,
  defaultCategory,
  defaultTags = [],
  ingestionContext,
  autoPickOnOpen = true,
  initialFile,
  initialFiles,
  onMonitoringExtracted,
}: {
  onClose: () => void;
  onSave?: (result: DocumentUploadSaveResult) => void;
  slot: DocumentSlotInfo;
  defaultCategory?: string | null;
  defaultTags?: string[];
  ingestionContext?: UploadJobSourceContext;
  autoPickOnOpen?: boolean;
  initialFile?: SelectedFile | null;
  initialFiles?: SelectedFile[] | null;
  onMonitoringExtracted?: (data: Record<string, unknown>) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("DocumentUploadModal");
  const [step, setStep] = useState<Step>("pick");
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    defaultCategory ?? null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ocrData, setOcrData] = useState<OCRData | null>(null);
  const [monitoringExtractedData, setMonitoringExtractedData] =
    useState<OCRData | null>(null);
  const [resultType, setResultType] = useState<string>("general");
  const [selectedDocType, setSelectedDocType] = useState<DocumentType>("other");
  const [docTypePickerVisible, setDocTypePickerVisible] = useState(false);
  const [queuedFiles, setQueuedFiles] = useState<SelectedFile[]>([]);
  const uploadIdRef = useRef<string | null>(null);
  const canonicalSourceAttachedRef = useRef(false);
  const canonicalRunReadyRef = useRef(false);
  const durableUploadJobIdRef = useRef<string | null>(null);
  const durableUploadJobItemsByNameRef = useRef<Map<string, string[]>>(
    new Map(),
  );
  const durableUploadJobItemRef = useRef<DurableUploadJobItem | null>(null);
  const primarySaveCompletedRef = useRef(false);
  const createdDocumentIdRef = useRef<string | null>(null);
  const savedMedicationRowIdsRef = useRef<Set<string>>(new Set());
  const savedAppointmentRowIdsRef = useRef<Set<string>>(new Set());
  const initialFileHandledRef = useRef<string | null>(null);
  const queuedFilesRef = useRef<SelectedFile[]>([]);

  const hasInitialFiles =
    Boolean(initialFiles && initialFiles.length > 0) || Boolean(initialFile);
  const shouldAutoPickOnOpen = autoPickOnOpen && !hasInitialFiles;
  const getIngestionContext = useCallback(
    (category: string | null): UploadJobSourceContext => {
      const targetHints = Array.from(
        new Set(
          [
            ...(ingestionContext?.targetHints ?? []),
            ...(defaultTags ?? []),
            slot?.id,
            category,
          ].filter((value): value is string => Boolean(value)),
        ),
      );

      return {
        ...ingestionContext,
        targetHints,
        allowBackgroundProcessing:
          ingestionContext?.allowBackgroundProcessing ?? !onMonitoringExtracted,
        allowAutomaticPromotion:
          ingestionContext?.allowAutomaticPromotion ??
          (slot == null && !onMonitoringExtracted),
      };
    },
    [defaultTags, ingestionContext, onMonitoringExtracted, slot],
  );

  // Form state for review — blood test
  const [resultRows, setResultRows] = useState<ResultRow[]>([]);
  const [testName, setTestName] = useState("");
  const [testDate, setTestDate] = useState("");
  const [labName, setLabName] = useState("");

  // Form state for review — imaging
  const [examName, setExamName] = useState("");
  const [examDate, setExamDate] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [imagingType, setImagingType] = useState("");
  const [findings, setFindings] = useState("");
  const [afc, setAfc] = useState("");
  const [afcUnit, setAfcUnit] = useState("follicles");
  const [endoThickness, setEndoThickness] = useState("");
  const [endoUnit, setEndoUnit] = useState("mm");

  // Form state for review — genetic
  const [geneticTestType, setGeneticTestType] = useState("");
  const [geneticResult, setGeneticResult] = useState("");
  const [geneticSummary, setGeneticSummary] = useState("");

  // Form state for review — semen
  const [semenVolume, setSemenVolume] = useState("");
  const [semenConcentration, setSemenConcentration] = useState("");
  const [semenMotility, setSemenMotility] = useState("");
  const [semenMorphology, setSemenMorphology] = useState("");
  const [semenNotes, setSemenNotes] = useState("");

  // Form state for review — prescription / treatment plan
  const [medicationRows, setMedicationRows] = useState<MedicationRow[]>([]);
  const [prescriber, setPrescriber] = useState("");
  const [prescriptionDate, setPrescriptionDate] = useState("");
  const [prescriptionClinic, setPrescriptionClinic] = useState("");
  const [appointmentRows, setAppointmentRows] = useState<AppointmentRow[]>([]);

  // Form state for review — general / document
  const [docTitle, setDocTitle] = useState("");
  const [docDate, setDocDate] = useState("");
  const [docProvider, setDocProvider] = useState("");
  const [docSummary, setDocSummary] = useState("");

  const resetExtractedState = useCallback(() => {
    uploadIdRef.current = null;
    canonicalSourceAttachedRef.current = false;
    canonicalRunReadyRef.current = false;
    durableUploadJobItemRef.current = null;
    primarySaveCompletedRef.current = false;
    createdDocumentIdRef.current = null;
    savedMedicationRowIdsRef.current = new Set();
    savedAppointmentRowIdsRef.current = new Set();
    setOcrData(null);
    setMonitoringExtractedData(null);
    setResultType("general");
    setSelectedDocType("other");
    setResultRows([]);
    setTestName("");
    setTestDate("");
    setLabName("");
    setExamName("");
    setExamDate("");
    setClinicName("");
    setImagingType("");
    setFindings("");
    setAfc("");
    setAfcUnit("follicles");
    setEndoThickness("");
    setEndoUnit("mm");
    setGeneticTestType("");
    setGeneticResult("");
    setGeneticSummary("");
    setSemenVolume("");
    setSemenConcentration("");
    setSemenMotility("");
    setSemenMorphology("");
    setSemenNotes("");
    setMedicationRows([]);
    setPrescriber("");
    setPrescriptionDate("");
    setPrescriptionClinic("");
    setAppointmentRows([]);
    setDocTitle("");
    setDocDate("");
    setDocProvider("");
    setDocSummary("");
  }, []);

  // API hooks
  const getUploadUrl = useGetUploadUrl();
  const uploadFile = useUploadFile();
  const completeUpload = useCompleteUpload();
  const createMedicationMutation = useCreateMedication();
  const createAppointmentMutation = useCreateAppointment();
  const { data: dbMedications = [] } = useMedications();
  const createTestResult = useCreateTestResult();
  const createTestResults = useCreateTestResults();

  const { client, patientId } = useApiClient();

  const registerDurableUploadJob = useCallback(
    (job: { id: string; items: { id: string; name: string }[] }) => {
      durableUploadJobIdRef.current = job.id;
      const itemsByName = new Map<string, string[]>();
      job.items.forEach((item) => {
        const itemIds = itemsByName.get(item.name) ?? [];
        itemIds.push(item.id);
        itemsByName.set(item.name, itemIds);
      });
      durableUploadJobItemsByNameRef.current = itemsByName;
    },
    [],
  );

  const takeDurableUploadJobItem = useCallback((fileName: string) => {
    const itemIds = durableUploadJobItemsByNameRef.current.get(fileName);
    const fileItemId = itemIds?.shift();
    if (!fileItemId || !durableUploadJobIdRef.current) return null;
    return {
      jobId: durableUploadJobIdRef.current,
      fileItemId,
    };
  }, []);

  // Prefill form fields from OCR data
  const prefillFromOCR = useCallback(
    (data: OCRData, type: string, sourceFileName?: string) => {
      const fileName =
        (sourceFileName ?? selectedFile?.name)?.replace(/\.[^.]+$/, "") ?? "";

      // Universal field helpers
      const title =
        (data.documentTitle as string) ||
        (data.testName as string) ||
        (data.examName as string) ||
        (data.reportName as string) ||
        fileName;
      const date =
        (data.documentDate as string) ||
        (data.testDate as string) ||
        (data.examDate as string) ||
        (data.reportDate as string) ||
        (data.prescriptionDate as string) ||
        "";
      const provider =
        (data.providerName as string) ||
        (data.labName as string) ||
        (data.clinicName as string) ||
        (data.insurerName as string) ||
        (data.provider as string) ||
        "";

      // Always prefill medications & appointments if present (regardless of type)
      const meds = (data.medications as Record<string, string>[]) || [];
      if (meds.length > 0) {
        setMedicationRows(
          meds.map((m, i) => ({
            id: `med-${i}`,
            name: m.medicationName || "",
            dosage: m.dosage || "",
            dosageUnit: m.dosageUnit || "",
            frequency: m.frequency || "",
            route: m.route || "",
            form: m.deliveryMethod || "",
            startDate: m.startDate || "",
            endDate:
              m.endDate ||
              endDateFromDuration(m.startDate || "", m.duration || ""),
            timeOfDay: m.timeOfDay || "",
          })),
        );
        setPrescriber((data.prescriber as string) || "");
        setPrescriptionDate(date);
        setPrescriptionClinic(provider);
      }

      const appts = (data.appointments as Record<string, string>[]) || [];
      if (appts.length > 0) {
        setAppointmentRows(
          appts.map((a, i) => ({
            id: `appt-${i}`,
            title: a.title || "",
            type:
              a.type && APPOINTMENT_TYPE_OPTIONS.includes(a.type)
                ? a.type
                : "exam",
            date: a.date || "",
            time: a.time || "",
            notes: a.notes || "",
          })),
        );
      }

      // Type-specific prefill
      switch (type) {
        case "blood": {
          setTestName(title);
          setTestDate(date);
          setLabName(provider);
          const results = (data.results as Record<string, string>[]) || [];
          const rows = results.map((r, i) => ({
            id: `ocr-${i}`,
            marker: r.marker || "",
            value: r.value || "",
            unit: r.unit || "",
            referenceRange: r.referenceRange || "",
          }));
          if (rows.length === 0) {
            rows.push({
              id: "empty-0",
              marker: "",
              value: "",
              unit: "",
              referenceRange: "",
            });
          }
          setResultRows(rows);
          break;
        }
        case "imaging":
          setExamName(title);
          setExamDate(date);
          setClinicName(provider);
          setImagingType((data.imagingType as string) || "");
          setFindings((data.findings as string) || "");
          setAfc((data.antralFollicleCount as string) || "");
          setEndoThickness((data.endometrialThickness as string) || "");
          break;
        case "genetic":
          setTestName(title);
          setTestDate(date);
          setLabName(provider);
          setGeneticTestType((data.testType as string) || "");
          setGeneticResult((data.result as string) || "");
          setGeneticSummary(
            (data.geneticSummary as string) || (data.summary as string) || "",
          );
          break;
        case "semen":
          setTestName(title);
          setTestDate(date);
          setLabName(provider);
          setSemenVolume((data.volume as string) || "");
          setSemenConcentration((data.concentration as string) || "");
          setSemenMotility((data.totalMotility as string) || "");
          setSemenMorphology((data.morphology as string) || "");
          setSemenNotes((data.notes as string) || "");
          break;
        case "prescription":
          // Meds & appts already prefilled above
          setPrescriptionDate(date);
          setPrescriptionClinic(provider);
          break;
        default: {
          setDocTitle(title);
          setDocDate(date);
          setDocProvider(provider);
          setDocSummary(
            (data.summary as string) ||
              (data.diagnosis as string) ||
              (data.recommendations as string) ||
              "",
          );
          break;
        }
      }
    },
    [selectedFile],
  );

  // Handle file selection — start upload + OCR
  const failedProcessMessage = t("documents.upload.error_failed_process");
  const handleFileSelected = useCallback(
    async (
      file: SelectedFile,
      category: string | null,
      durableJobItem: DurableUploadJobItem | null = null,
    ) => {
      resetExtractedState();
      setSelectedFile(file);
      setSelectedCategory(category);
      setErrorMessage(null);
      setStep("uploading");

      const destination = destinationForUploadContext({
        slotId: slot?.id,
        category,
      });
      const contentType = resolveUploadContentType(file);

      try {
        if (!patientId) {
          throw new Error("Document upload requires an active patient");
        }

        // Every source must have a durable job/item before bytes are
        // transferred. The processing worker, not this modal, owns the
        // document-intelligence run after source acknowledgement.
        durableUploadJobItemRef.current =
          durableJobItem ?? takeDurableUploadJobItem(file.name);
        if (!durableUploadJobItemRef.current) {
          const job = await createUploadJob(client, patientId, {
            clientRequestId: createUploadJobIdempotencyKey(),
            source: "picker",
            sourceChannel: "patient_ui",
            intent: slot || onMonitoringExtracted ? "prefill" : "dump_anything",
            sourceContext: getIngestionContext(category),
            items: [
              {
                name: file.name,
                contentType,
                sizeBytes: file.size ?? null,
                destination,
              },
            ],
          });
          registerDurableUploadJob(job);
          durableUploadJobItemRef.current = takeDurableUploadJobItem(file.name);
        }
        if (!durableUploadJobItemRef.current) {
          throw new Error("Document upload job item could not be created");
        }

        // Step 1: Upload file to S3
        const { uploadId, uploadUrl } = await getUploadUrl.mutateAsync({
          name: file.name,
          contentType,
        });

        const fileData = await readFileForUpload({
          uri: file.uri,
          name: file.name,
          mimeType: file.mimeType,
        });
        await uploadFile.mutateAsync({
          uploadUrl,
          file: fileData,
          contentType,
        });

        await completeUpload.mutateAsync(uploadId);
        uploadIdRef.current = uploadId;
        setStep("reading");

        const currentDurableJobItem = durableUploadJobItemRef.current;
        if (!currentDurableJobItem) {
          throw new Error("Document upload job item is unavailable");
        }
        await attachUploadJobSourceUpload(
          client,
          patientId,
          currentDurableJobItem.jobId,
          currentDurableJobItem.fileItemId,
          { uploadId },
        );
        canonicalSourceAttachedRef.current = true;

        // Contextual forms may wait briefly for the canonical run. General
        // uploads are already durably saved and continue through the global
        // activity surface without inventing a caller-shaped OCR result.
        const needsContextualPrefill = Boolean(slot || onMonitoringExtracted);
        if (!needsContextualPrefill) {
          onSave?.({
            fileName: file.name,
            resultType: "general",
            category,
            uploadId,
            documentId: null,
          });
          setStep("success");
          if (queuedFiles.length === 0) {
            setTimeout(() => onClose(), 1200);
          }
          return;
        }

        const result = await waitForDocumentProcessingRun(
          client,
          patientId,
          currentDurableJobItem.jobId,
          currentDurableJobItem.fileItemId,
        );
        const runSucceeded = result?.status === "succeeded";
        canonicalRunReadyRef.current = runSucceeded;
        if (result && !runSucceeded) {
          setErrorMessage(failedProcessMessage);
          if (onMonitoringExtracted) {
            setStep("processingError");
          } else {
            setStep("review");
          }
          return;
        }
        const projectedResult =
          result && runSucceeded
            ? projectDocumentProcessingRunToPrefill(result, file.name)
            : null;

        if (projectedResult) {
          const type = detectResultType(projectedResult);
          setOcrData(projectedResult);
          setResultType(type);
          const labelFromProcessing = projectedResult.documentLabel;
          const validLabels: DocumentType[] = [
            "lab",
            "imaging",
            "treatment_plan",
            "appointment_letter",
            "genetic",
            "other",
          ];
          const selectedLabel =
            typeof labelFromProcessing === "string" &&
            validLabels.includes(labelFromProcessing as DocumentType)
              ? (labelFromProcessing as DocumentType)
              : null;
          setSelectedDocType(selectedLabel ?? ocrTypeToDocType(type));
          prefillFromOCR(projectedResult, type, file.name);
          if (onMonitoringExtracted) {
            setMonitoringExtractedData(projectedResult);
            onMonitoringExtracted(projectedResult);
            setStep("monitoringParsed");
            return;
          }
        } else {
          const today = "";
          const name = file.name.replace(/\.[^.]+$/, "");
          setResultType("general");
          setSelectedDocType("other");
          setDocTitle(name);
          setDocDate(today);
          if (onMonitoringExtracted) {
            onMonitoringExtracted({});
            setMonitoringExtractedData(null);
            setStep("monitoringParsed");
            return;
          }
        }

        setStep("review");
      } catch (error) {
        console.warn("Canonical document processing failed:", error);

        // A source that has been acknowledged is still durable even if the
        // processing read times out. An upload that failed before source
        // acknowledgement must return to pick; it is never presented as
        // successfully handed off.
        if (uploadIdRef.current && canonicalSourceAttachedRef.current) {
          const name = file.name.replace(/\.[^.]+$/, "");
          setResultType("general");
          setSelectedDocType("other");
          setDocTitle(name);
          setDocDate("");
          if (onMonitoringExtracted) {
            onMonitoringExtracted({});
            setMonitoringExtractedData(null);
            setStep("monitoringParsed");
          } else {
            setStep("review");
          }
        } else {
          setErrorMessage(
            getDocumentUploadErrorMessage(error, failedProcessMessage),
          );
          setStep(onMonitoringExtracted ? "processingError" : "pick");
        }
      } finally {
        revokeSelectedFileObjectUrl(file);
      }
    },
    [
      slot,
      getUploadUrl,
      uploadFile,
      completeUpload,
      patientId,
      prefillFromOCR,
      failedProcessMessage,
      resetExtractedState,
      onMonitoringExtracted,
      onClose,
      onSave,
      queuedFiles.length,
      client,
      registerDurableUploadJob,
      takeDurableUploadJobItem,
      getIngestionContext,
    ],
  );

  const handleFilesSelected = useCallback(
    (files: SelectedFile[], category: string | null) => {
      if (files.length === 0) return;
      const [firstFile, ...restFiles] = files;
      queuedFilesRef.current.forEach(revokeSelectedFileObjectUrl);
      durableUploadJobIdRef.current = null;
      durableUploadJobItemsByNameRef.current = new Map();
      setQueuedFiles(restFiles);

      void (async () => {
        if (!patientId) {
          setErrorMessage("Document upload requires an active patient");
          setStep("pick");
          return;
        }
        const destination = destinationForUploadContext({
          slotId: slot?.id,
          category,
        });
        const job = await createUploadJob(client, patientId, {
          clientRequestId: createUploadJobIdempotencyKey(),
          source: "picker",
          sourceChannel: "patient_ui",
          intent: slot || onMonitoringExtracted ? "prefill" : "dump_anything",
          sourceContext: getIngestionContext(category),
          items: files.map((file) => ({
            name: file.name,
            contentType: resolveUploadContentType(file),
            sizeBytes: file.size ?? null,
            destination,
          })),
        });
        registerDurableUploadJob(job);

        void handleFileSelected(firstFile!, category);
      })().catch((error) => {
        setErrorMessage(
          getDocumentUploadErrorMessage(error, failedProcessMessage),
        );
        setStep("pick");
      });
    },
    [
      client,
      failedProcessMessage,
      handleFileSelected,
      onMonitoringExtracted,
      patientId,
      registerDurableUploadJob,
      slot,
      getIngestionContext,
    ],
  );

  const handleAddMoreFiles = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });
      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      setQueuedFiles((current) => [
        ...current,
        ...result.assets.map((file) => ({
          name: file.name,
          uri: file.uri,
          mimeType: resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
          size: file.size ?? undefined,
        })),
      ]);
    } catch (error) {
      console.error("Error adding more document files:", error);
    }
  }, []);

  useEffect(() => {
    queuedFilesRef.current = queuedFiles;
  }, [queuedFiles]);

  useEffect(
    () => () => {
      queuedFilesRef.current.forEach(revokeSelectedFileObjectUrl);
    },
    [],
  );

  useEffect(() => {
    if (step !== "pick") return;
    const files =
      initialFiles && initialFiles.length > 0
        ? initialFiles
        : initialFile
          ? [initialFile]
          : [];
    if (files.length === 0) return;

    const filesKey = files.map((file) => file.uri).join("|");
    if (initialFileHandledRef.current === filesKey) return;
    initialFileHandledRef.current = filesKey;
    handleFilesSelected(files, selectedCategory);
  }, [handleFilesSelected, initialFile, initialFiles, selectedCategory, step]);

  const chooseAnotherMonitoringFile = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });
      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }
      setErrorMessage(null);
      handleFilesSelected(
        result.assets.map((file) => ({
          name: file.name,
          uri: file.uri,
          mimeType: resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
          size: file.size ?? undefined,
        })),
        selectedCategory,
      );
    } catch (error) {
      console.error("Error picking replacement monitoring document:", error);
    }
  }, [handleFilesSelected, selectedCategory]);

  // Save the document + test results
  const handleSave = useCallback(async () => {
    setStep("saving");
    setErrorMessage(null);

    try {
      const uploadId = uploadIdRef.current;

      // Pre-validate the user-editable med and appointment rows BEFORE we
      // touch the API. Silently skipping a row used to make the appointment
      // / med vanish without explanation; instead block the save and tell
      // the user exactly which row is incomplete.
      const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
      const ISO_TIME = /^\d{2}:\d{2}$/;
      const invalidMed = medicationRows.find(
        (m) => m.name.trim() && (!m.startDate || !ISO_DATE.test(m.startDate)),
      );
      if (invalidMed) {
        setErrorMessage(
          t("documents.upload.error_med_missing_start_date", {
            params: { name: invalidMed.name.trim() },
          }),
        );
        setStep("review");
        return;
      }
      const invalidAppt = appointmentRows.find(
        (a) =>
          a.title.trim() &&
          (!a.date ||
            !ISO_DATE.test(a.date) ||
            !a.time ||
            !ISO_TIME.test(a.time)),
      );
      if (invalidAppt) {
        setErrorMessage(
          t("documents.upload.error_appt_missing_date_time", {
            params: { title: invalidAppt.title.trim() },
          }),
        );
        setStep("review");
        return;
      }

      if (!primarySaveCompletedRef.current) {
        // The canonical source orchestration has already persisted the
        // original file and owns the durable document root. Contextual form
        // saves below only promote user-confirmed domain rows; creating a
        // second patient document here would duplicate the source attachment.
        if (
          resultType === "blood" ||
          resultType === "imaging" ||
          resultType === "genetic" ||
          resultType === "semen"
        ) {
          const basePayload = {
            name:
              resultType === "blood" ||
              resultType === "genetic" ||
              resultType === "semen"
                ? testName
                : examName,
            date:
              resultType === "blood" ||
              resultType === "genetic" ||
              resultType === "semen"
                ? testDate
                : examDate,
            attachmentId: uploadId || null,
          };

          switch (resultType) {
            case "blood": {
              const validRows = resultRows.filter((row) => row.marker.trim());
              if (validRows.length > 0) {
                await createTestResults.mutateAsync(
                  validRows.map((row) => ({
                    ...basePayload,
                    type: "blood" as TestResultType,
                    bloodTestCategory: "hormonal" as BloodTestCategory,
                    name: row.marker,
                    patientValue: row.value || null,
                    unit: row.unit || null,
                    valueType: "numeric" as const,
                  })),
                );
              } else {
                await createTestResult.mutateAsync({
                  ...basePayload,
                  type: "blood" as TestResultType,
                  bloodTestCategory: "hormonal" as BloodTestCategory,
                });
              }
              break;
            }
            case "imaging": {
              const imagingNotes = [
                imagingType &&
                  t("documents.imaging_notes.type_line", {
                    params: { value: imagingType },
                  }),
                findings &&
                  t("documents.imaging_notes.findings_line", {
                    params: { value: findings },
                  }),
                afc &&
                  t("documents.imaging_notes.afc_line", {
                    params: { value: afc },
                  }),
                endoThickness &&
                  t("documents.imaging_notes.endo_thickness_line", {
                    params: { value: endoThickness },
                  }),
              ]
                .filter(Boolean)
                .join("\n\n");

              await createTestResult.mutateAsync({
                ...basePayload,
                type: "imaging" as TestResultType,
                imagingTestCategory: (imagingType
                  ?.toLowerCase()
                  .includes("ultrasound")
                  ? "transvaginal_ultrasound"
                  : "other") as ImagingTestCategory,
                notes: imagingNotes || null,
                patientValue: afc || null,
                unit: afc ? afcUnit || "follicles" : null,
              });
              break;
            }
            case "genetic": {
              const geneticNotes = [
                geneticTestType &&
                  t("documents.genetic_notes.test_type_line", {
                    params: { value: geneticTestType },
                  }),
                geneticSummary &&
                  t("documents.genetic_notes.summary_line", {
                    params: { value: geneticSummary },
                  }),
              ]
                .filter(Boolean)
                .join("\n\n");

              await createTestResult.mutateAsync({
                ...basePayload,
                type: "genetics" as TestResultType,
                geneticsTestCategory: (geneticTestType
                  ?.toLowerCase()
                  .includes("karyotype")
                  ? "karyotype"
                  : geneticTestType?.toLowerCase().includes("carrier")
                    ? "carrier_testing"
                    : "other") as GeneticsTestCategory,
                notes: geneticNotes || null,
              });
              break;
            }
            case "semen": {
              const semenResults = [
                semenVolume && {
                  name: t("documents.semen_result.volume"),
                  value: semenVolume,
                  unit: "mL",
                },
                semenConcentration && {
                  name: t("documents.semen_result.concentration"),
                  value: semenConcentration,
                  unit: "M/mL",
                },
                semenMotility && {
                  name: t("documents.semen_result.motility"),
                  value: semenMotility,
                  unit: "%",
                },
                semenMorphology && {
                  name: t("documents.semen_result.morphology"),
                  value: semenMorphology,
                  unit: "%",
                },
              ].filter(Boolean) as {
                name: string;
                value: string;
                unit: string;
              }[];

              if (semenResults.length > 0) {
                await createTestResults.mutateAsync(
                  semenResults.map((row) => ({
                    ...basePayload,
                    type: "semenFluidAnalysis" as TestResultType,
                    semenFluidTestCategory: "basic" as const,
                    name: row.name,
                    patientValue: row.value,
                    unit: row.unit,
                    valueType: "numeric" as const,
                  })),
                );
              } else {
                await createTestResult.mutateAsync({
                  ...basePayload,
                  type: "semenFluidAnalysis" as TestResultType,
                  semenFluidTestCategory: "basic" as const,
                });
              }
              break;
            }
          }
        }
        primarySaveCompletedRef.current = true;
      }

      const failedCareRows: string[] = [];

      // Create medications in the schedule
      const medsToCreate = medicationRows.filter(
        (m) =>
          m.name.trim() &&
          m.startDate &&
          !savedMedicationRowIdsRef.current.has(m.id),
      );
      for (const med of medsToCreate) {
        try {
          const dbMed = dbMedications.find(
            (m) => m.name?.toLowerCase() === med.name.toLowerCase(),
          );
          await createMedicationMutation.mutateAsync({
            name: med.name,
            startDate: med.startDate,
            // An end date before the start is contradictory OCR output;
            // dropping it beats persisting an impossible course (#2220).
            endDate:
              med.endDate && med.endDate >= med.startDate ? med.endDate : null,
            dose: med.dosage ? parseFloat(med.dosage) : null,
            doseUnit: med.dosageUnit || null,
            timeTaken: med.timeOfDay ? [med.timeOfDay] : undefined,
            route: serializeMedicationRoute(med.route),
            form: serializeMedicationForm(med.form),
            notes: med.frequency || null,
            ...(dbMed && { medicationId: dbMed.id }),
          });
          savedMedicationRowIdsRef.current.add(med.id);
        } catch (medError) {
          console.error("Failed to create medication:", med.name, medError);
          failedCareRows.push(
            `${t("documents.upload.derived_medication_label")}: ${med.name}`,
          );
        }
      }

      // Create appointments in the schedule. Previously these were only
      // stored as `appt:` tags on the document — invisible from the calendar.
      const apptsToCreate = appointmentRows.filter(
        (a) => a.title.trim() && !savedAppointmentRowIdsRef.current.has(a.id),
      );
      for (const appt of apptsToCreate) {
        // Derive a 1-hour end window so the backend, which requires
        // endDate/endTime, accepts the appointment.
        const parts = appt.time.split(":").map(Number);
        const hh = parts[0] ?? 0;
        const mm = parts[1] ?? 0;
        const endTime =
          hh >= 23
            ? "23:59"
            : `${String(hh + 1).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
        const payload = {
          title: appt.title,
          consultationType: appt.type ? [appt.type] : null,
          startDate: appt.date,
          startTime: appt.time,
          endDate: appt.date,
          endTime,
          description: appt.notes || null,
        };
        try {
          await createAppointmentMutation.mutateAsync(payload);
          savedAppointmentRowIdsRef.current.add(appt.id);
        } catch (apptError) {
          console.error(
            "[DocumentUploadModal] Failed to create appointment:",
            appt.title,
            apptError,
          );
          failedCareRows.push(
            `${t("documents.upload.derived_appointment_label")}: ${appt.title}`,
          );
        }
      }

      if (failedCareRows.length > 0) {
        setErrorMessage(
          t("documents.upload.error_derived_rows_failed", {
            params: { items: failedCareRows.join(", ") },
          }),
        );
        setStep("review");
        return;
      }

      const durableJobId = durableUploadJobIdRef.current;
      if (durableJobId && patientId && canonicalRunReadyRef.current) {
        try {
          await sendUploadJobCommand(client, patientId, durableJobId, {
            type: "acknowledge_job",
            idempotencyKey: createUploadJobIdempotencyKey(),
          });
        } catch (jobError) {
          // Saving the destination record remains successful even if the
          // activity acknowledgement is delayed; the next poll can still
          // recover the durable state.
          console.warn(
            "[DocumentUploadModal] Could not acknowledge durable upload job",
            jobError,
          );
        }
      }

      onSave?.({
        fileName: selectedFile?.name ?? "",
        resultType,
        category: selectedCategory,
        uploadId: uploadId ?? null,
        documentId: createdDocumentIdRef.current,
      });
      setStep("success");
      if (queuedFiles.length === 0) {
        setTimeout(() => onClose(), 1200);
      }
    } catch (error) {
      console.error("Save failed:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("documents.upload.error_failed_save"),
      );
      setStep("review");
    }
  }, [
    selectedCategory,
    resultType,
    medicationRows,
    appointmentRows,
    testName,
    testDate,
    examName,
    examDate,
    resultRows,
    imagingType,
    findings,
    afc,
    afcUnit,
    endoThickness,
    geneticTestType,
    geneticSummary,
    semenVolume,
    semenConcentration,
    semenMotility,
    semenMorphology,
    createTestResult,
    createTestResults,
    createMedicationMutation,
    createAppointmentMutation,
    dbMedications,
    onClose,
    onSave,
    queuedFiles.length,
    selectedFile?.name,
    t,
    client,
    patientId,
  ]);

  const handleNextQueuedFile = useCallback(() => {
    const [nextFile, ...restFiles] = queuedFiles;
    if (!nextFile) {
      onClose();
      return;
    }
    setQueuedFiles(restFiles);
    void handleFileSelected(nextFile, selectedCategory);
  }, [handleFileSelected, onClose, queuedFiles, selectedCategory]);

  const processingItems: DocumentUploadProcessingItem[] = selectedFile
    ? [
        {
          id: selectedFile.uri,
          name: selectedFile.name,
          detail:
            step === "uploading"
              ? [
                  formatFileSize(selectedFile.size),
                  t("documents.upload.secure_upload"),
                ]
                  .filter(Boolean)
                  .join(" · ")
              : t("documents.upload.upload_complete_reading"),
          phase: step === "uploading" ? "uploading" : "reading",
        },
        ...queuedFiles.map((file) => ({
          id: file.uri,
          name: file.name,
          detail: t("documents.upload.queued_file_added"),
          phase: "queued" as const,
        })),
      ]
    : [];

  // ── Render steps ──

  if (
    step === "monitoringParsed" ||
    (step === "saving" && onMonitoringExtracted)
  ) {
    return (
      <MonitoringParsedStep
        onClose={onClose}
        fileName={selectedFile?.name ?? ""}
        valueCount={monitoringValueCount(monitoringExtractedData)}
        queuedFiles={queuedFiles}
        saving={step === "saving"}
        onContinue={() => void handleSave()}
      />
    );
  }

  if (step === "success") {
    return (
      <>
        <CloseButton onClose={onClose} />
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            paddingVertical: 32,
            gap: 12,
          }}
        >
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              backgroundColor: EMBER.colors.green,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Check size={24} color="#fff" strokeWidth={2.5} />
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 16,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {t("documents.upload.saved")}
          </Text>
          {resultType === "blood" && resultRows.length > 0 && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                textAlign: "center",
              }}
            >
              {t("documents.upload.results_added_count", {
                params: {
                  count: String(
                    resultRows.filter((r) => r.marker.trim()).length,
                  ),
                },
              })}
            </Text>
          )}
          {resultType === "prescription" && medicationRows.length > 0 && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textLight,
                textAlign: "center",
              }}
            >
              {t("documents.upload.medications_detected_count", {
                params: {
                  count: String(
                    medicationRows.filter((m) => m.name.trim()).length,
                  ),
                },
              })}
            </Text>
          )}
          {queuedFiles.length > 0 ? (
            <>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                }}
              >
                {t("documents.upload.more_documents_pending_count", {
                  params: { count: String(queuedFiles.length) },
                })}
              </Text>
              <View style={{ width: "100%", marginTop: 10 }}>
                <QueuedDocumentsCard files={queuedFiles} />
              </View>
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("documents.upload.review_next_document")),
                    "documents.upload.review_next_document",
                  );
                  handleNextQueuedFile();
                }}
                style={{
                  marginTop: 4,
                  backgroundColor: EMBER.colors.warm,
                  paddingHorizontal: 18,
                  paddingVertical: 10,
                  borderRadius: 10,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 13, fontWeight: "600", color: "#fff" }}
                >
                  {t("documents.upload.review_next_document")}
                </Text>
              </Pressable>
            </>
          ) : null}
        </View>
      </>
    );
  }

  if (step === "pick") {
    return (
      <PickStep
        onClose={onClose}
        slot={slot}
        defaultCategory={defaultCategory}
        onFilesSelected={handleFilesSelected}
        errorMessage={errorMessage}
        autoPickOnOpen={shouldAutoPickOnOpen}
      />
    );
  }

  if (step === "uploading" || step === "reading") {
    return (
      <>
        <CloseButton onClose={onClose} />
        <DocumentUploadProcessingStep
          phase={step === "uploading" ? "uploading" : "reading"}
          items={processingItems}
          onAddMoreFiles={() => void handleAddMoreFiles()}
        />
      </>
    );
  }

  if (step === "processingError" && onMonitoringExtracted) {
    return (
      <ProcessingErrorStep
        onClose={onClose}
        fileName={selectedFile?.name ?? ""}
        message={errorMessage ?? failedProcessMessage}
        onChooseAnother={() => void chooseAnotherMonitoringFile()}
      />
    );
  }

  // Review step
  return (
    <>
      {/* Back + close */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Pressable
          onPress={() => {
            // Chevron with the back copy beside it.
            capturePress(pressLabel(t("documents.upload.back")), "upload_back");
            setStep("pick");
            setSelectedFile(null);
            uploadIdRef.current = null;
          }}
          hitSlop={8}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ChevronLeft size={16} color={EMBER.colors.warm} strokeWidth={2} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.warm,
              fontWeight: "500",
            }}
          >
            {t("documents.upload.back")}
          </Text>
        </Pressable>
        <CloseButton onClose={onClose} />
      </View>

      {/* Review header */}
      <View style={{ marginBottom: 14, paddingRight: 32 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Edit3 size={16} color={EMBER.colors.warm} strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {t("documents.upload.review_title")}
          </Text>
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textLight,
            marginTop: 4,
            lineHeight: 18,
          }}
        >
          {t("documents.upload.review_subtitle")}
        </Text>
      </View>

      {/* File indicator */}
      {selectedFile && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            padding: 10,
            borderRadius: 8,
            backgroundColor: "rgba(0,0,0,0.02)",
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.05)",
            marginBottom: 14,
          }}
        >
          <FileText size={14} color={EMBER.colors.warm} strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              flex: 1,
              fontSize: 12,
              color: EMBER.colors.textMid,
            }}
          >
            {selectedFile.name}
          </Text>
          {selectedFile.size ? (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.textFaint }}
            >
              {formatFileSize(selectedFile.size)}
            </Text>
          ) : null}
        </View>
      )}

      <QueuedDocumentsCard files={queuedFiles} />

      {/* Document type picker */}
      <View style={{ marginBottom: 14 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            color: EMBER.colors.textLight,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            marginBottom: 6,
          }}
        >
          {t("documents.upload.doc_type_label")}
        </Text>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t(DOCUMENT_TYPE_META[selectedDocType].labelKey)),
              "open_document_type_picker",
            );
            setDocTypePickerVisible(true);
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            padding: 10,
            borderRadius: 8,
            backgroundColor: `${DOCUMENT_TYPE_META[selectedDocType].color}08`,
            borderWidth: 1,
            borderColor: `${DOCUMENT_TYPE_META[selectedDocType].color}20`,
          }}
        >
          {(() => {
            const meta = DOCUMENT_TYPE_META[selectedDocType];
            const Icon = meta.icon;
            return (
              <>
                <View
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    backgroundColor: `${meta.color}14`,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={14} color={meta.color} strokeWidth={1.5} />
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: "600",
                    color: meta.color,
                  }}
                >
                  {t(meta.labelKey)}
                </Text>
                <ChevronDown
                  size={14}
                  color={EMBER.colors.textFaint}
                  strokeWidth={2}
                />
              </>
            );
          })()}
        </Pressable>
      </View>

      {/* Document type picker uses the shared frosted-glass dialog chrome. */}
      <Dialog
        visible={docTypePickerVisible}
        onClose={() => setDocTypePickerVisible(false)}
        animationType="fade"
        containerStyle={{ width: 260, maxWidth: 260 }}
        contentStyle={{ padding: 8 }}
        testID="document-type-picker-dialog"
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "700",
            color: EMBER.colors.text,
            paddingHorizontal: 10,
            paddingVertical: 8,
          }}
        >
          {t("documents.doc_type.choose")}
        </Text>
        {ALL_DOCUMENT_TYPES.map((type) => {
          const typeMeta = DOCUMENT_TYPE_META[type];
          const TypeIcon = typeMeta.icon;
          const isSelected = type === selectedDocType;
          return (
            <Pressable
              key={type}
              onPress={() => {
                capturePress(pressLabel(t(typeMeta.labelKey)), "document_type");
                setSelectedDocType(type);
                setDocTypePickerVisible(false);
                // Picking a category clears empty rows of the OTHER kind
                // (OCR sometimes returns an empty placeholder medication
                // even on an appointment doc, and vice versa). It then
                // seeds one row of the chosen kind, pre-filled with the
                // general OCR fields so the section isn't blank.
                if (type === "appointment_letter") {
                  setMedicationRows(
                    medicationRows.filter((m) => m.name.trim().length > 0),
                  );
                  if (appointmentRows.length === 0) {
                    setAppointmentRows([
                      {
                        id: `appt-${Date.now()}`,
                        title: docTitle || "",
                        type: "consult",
                        date: docDate || "",
                        time: "",
                        notes: docSummary || "",
                      },
                    ]);
                  }
                }
                if (type === "treatment_plan") {
                  setAppointmentRows(
                    appointmentRows.filter((a) => a.title.trim().length > 0),
                  );
                  if (medicationRows.length === 0) {
                    setMedicationRows([
                      {
                        id: `med-${Date.now()}`,
                        name: "",
                        dosage: "",
                        dosageUnit: "",
                        frequency: "",
                        route: "",
                        form: "",
                        startDate: docDate || "",
                        endDate: "",
                        timeOfDay: "",
                      },
                    ]);
                  }
                }
              }}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingHorizontal: 10,
                paddingVertical: 10,
                borderRadius: 8,
                backgroundColor: isSelected
                  ? `${typeMeta.color}10`
                  : "transparent",
              }}
            >
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  backgroundColor: `${typeMeta.color}14`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TypeIcon size={14} color={typeMeta.color} strokeWidth={1.5} />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: isSelected ? "700" : "500",
                  color: isSelected ? typeMeta.color : EMBER.colors.text,
                }}
              >
                {t(typeMeta.labelKey)}
              </Text>
              {isSelected && (
                <Check size={14} color={typeMeta.color} strokeWidth={2.5} />
              )}
            </Pressable>
          );
        })}
      </Dialog>

      {/* Error */}
      {errorMessage && <ErrorBanner message={errorMessage} />}

      {/* Review form — scrollable */}
      <ScrollView
        style={{ maxHeight: 340 }}
        showsVerticalScrollIndicator={false}
      >
        {resultType === "blood" && (
          <BloodTestReview
            data={ocrData ?? {}}
            rows={resultRows}
            setRows={setResultRows}
            testName={testName}
            setTestName={setTestName}
            testDate={testDate}
            setTestDate={setTestDate}
            labName={labName}
            setLabName={setLabName}
          />
        )}
        {resultType === "imaging" && (
          <ImagingReview
            examName={examName}
            setExamName={setExamName}
            examDate={examDate}
            setExamDate={setExamDate}
            clinicName={clinicName}
            setClinicName={setClinicName}
            imagingType={imagingType}
            setImagingType={setImagingType}
            findings={findings}
            setFindings={setFindings}
            afc={afc}
            setAfc={setAfc}
            afcUnit={afcUnit}
            setAfcUnit={setAfcUnit}
            endoThickness={endoThickness}
            setEndoThickness={setEndoThickness}
            endoUnit={endoUnit}
            setEndoUnit={setEndoUnit}
          />
        )}
        {resultType === "genetic" && (
          <GeneticReview
            testName={testName}
            setTestName={setTestName}
            testDate={testDate}
            setTestDate={setTestDate}
            labName={labName}
            setLabName={setLabName}
            testType={geneticTestType}
            setTestType={setGeneticTestType}
            result={geneticResult}
            setResult={setGeneticResult}
            summary={geneticSummary}
            setSummary={setGeneticSummary}
          />
        )}
        {resultType === "semen" && (
          <SemenReview
            testName={testName}
            setTestName={setTestName}
            testDate={testDate}
            setTestDate={setTestDate}
            labName={labName}
            setLabName={setLabName}
            volume={semenVolume}
            setVolume={setSemenVolume}
            concentration={semenConcentration}
            setConcentration={setSemenConcentration}
            motility={semenMotility}
            setMotility={setSemenMotility}
            morphology={semenMorphology}
            setMorphology={setSemenMorphology}
            notes={semenNotes}
            setNotes={setSemenNotes}
          />
        )}
        {resultType === "prescription" && (
          <PrescriptionReview
            medications={medicationRows}
            setMedications={setMedicationRows}
            appointments={appointmentRows}
            setAppointments={setAppointmentRows}
            prescriber={prescriber}
            setPrescriber={setPrescriber}
            date={prescriptionDate}
            setDate={setPrescriptionDate}
            clinic={prescriptionClinic}
            setClinic={setPrescriptionClinic}
          />
        )}
        {/* Show medications & appointments alongside any primary type when
            detected, OR when the user manually chose treatment_plan /
            appointment_letter so they can fill the rows in even if OCR
            didn't recognise the document. */}
        {resultType !== "prescription" &&
          (medicationRows.length > 0 ||
            appointmentRows.length > 0 ||
            selectedDocType === "treatment_plan" ||
            selectedDocType === "appointment_letter") && (
            <PrescriptionReview
              medications={medicationRows}
              setMedications={setMedicationRows}
              appointments={appointmentRows}
              setAppointments={setAppointmentRows}
              prescriber={prescriber}
              setPrescriber={setPrescriber}
              date={prescriptionDate}
              setDate={setPrescriptionDate}
              clinic={prescriptionClinic}
              setClinic={setPrescriptionClinic}
            />
          )}
        {(resultType === "general" ||
          resultType === "insurance" ||
          resultType === "compliance") &&
          // Hide the general doc-meta form when the user is filling out a
          // prescription / appointment — those flows have their own date and
          // notes fields inside PrescriptionReview, so the general fields
          // would just be redundant clutter.
          selectedDocType !== "treatment_plan" &&
          selectedDocType !== "appointment_letter" && (
            <GeneralReview
              title={docTitle}
              setTitle={setDocTitle}
              date={docDate}
              setDate={setDocDate}
              provider={docProvider}
              setProvider={setDocProvider}
              summary={docSummary}
              setSummary={setDocSummary}
            />
          )}
      </ScrollView>

      {/* Save button */}
      <BrownButton
        label={
          step === "saving"
            ? t("documents.upload.saving")
            : primarySaveCompletedRef.current
              ? t("documents.upload.retry_care_rows")
              : t("documents.upload.save")
        }
        onPress={handleSave}
        loading={step === "saving"}
        style={{ marginTop: 16 }}
      />
    </>
  );
}

// ── Exported Modal ──

export default function DocumentUploadModal({
  visible,
  onClose,
  onSave,
  slot = null,
  defaultCategory,
  defaultTags,
  ingestionContext,
  autoPickOnOpen,
  initialFile,
  initialFiles,
  onMonitoringExtracted,
}: Props) {
  if (!visible) return null;

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      animationType="fade"
      containerStyle={{
        width: "90%",
        maxWidth: onMonitoringExtracted ? 390 : 420,
        maxHeight: "85%",
      }}
      surfaceStyle={{ maxHeight: "100%" }}
      contentStyle={{
        paddingHorizontal: 24,
        paddingTop: 28,
        paddingBottom: 24,
      }}
      testID="document-upload-dialog"
      backdropTestID="document-upload-dialog-backdrop"
    >
      <ModalContent
        onClose={onClose}
        onSave={onSave}
        slot={slot}
        defaultCategory={defaultCategory}
        defaultTags={defaultTags}
        ingestionContext={ingestionContext}
        autoPickOnOpen={autoPickOnOpen}
        initialFile={initialFile}
        initialFiles={initialFiles}
        onMonitoringExtracted={onMonitoringExtracted}
      />
    </Dialog>
  );
}
